'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { getFeaturedProducts, type ApiProduct } from '@/lib/api';

interface FeaturedProductsProps {
  className?: string;
}

const FeaturedProducts = ({ className = '' }: FeaturedProductsProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ApiProduct[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getFeaturedProducts()
      .then((data) => {
        if (cancelled) return;
        setItems(data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Не удалось загрузить товары'
        );
        setItems([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const formatPrice = (price: number): string => {
    return Number(price).toLocaleString('ru-KZ') + ' ₸';
  };

  const rows = useMemo(() => {
    return items.map((product) => {
      const pack = product.purchaseOptions.find((o) => o.type === 'pack');
      const box = product.purchaseOptions.find((o) => o.type === 'box');
      return {
        product,
        pack,
        box,
        categoryLabel: product.categoryName || product.category,
        href: `/product-catalog?category=${encodeURIComponent(product.category)}`,
      };
    });
  }, [items]);

  if (loading) {
    return (
      <section className={`bg-background py-12 lg:py-16 ${className}`}>
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <div className="mb-8 flex items-center justify-between">
            <h2 className="font-heading text-2xl font-semibold text-text-primary lg:text-3xl">
              Популярные товары
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="overflow-hidden rounded-md bg-card shadow-sm">
                <div className="aspect-square bg-muted" />
                <div className="p-4">
                  <div className="mb-2 h-4 w-24 rounded bg-muted" />
                  <div className="mb-4 h-6 w-full rounded bg-muted" />
                  <div className="h-10 w-full rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`bg-background py-12 lg:py-16 ${className}`}>
      <div className="mx-auto max-w-7xl px-4 lg:px-6">
        <div className="mb-8 flex items-center justify-between">
          <h2 className="font-heading text-2xl font-semibold text-text-primary lg:text-3xl">
            Популярные товары
          </h2>
          <Link
            href="/product-catalog"
            className="flex items-center gap-1 font-heading text-sm font-medium text-accent transition-colors duration-250 hover:text-accent/80 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            <span>Все товары</span>
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>

        {error && (
          <p className="mb-6 font-body text-sm text-error" role="alert">
            {error}
          </p>
        )}

        {rows.length === 0 && !error ? (
          <p className="font-body text-sm text-text-secondary">
            В каталоге пока нет товаров для блока на главной.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {rows.map(({ product, pack, box, categoryLabel, href }) => (
              <Link
                key={product.id}
                href={href}
                className="group overflow-hidden rounded-md bg-card shadow-sm transition-all duration-250 hover:-translate-y-1 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
              >
                <div className="relative aspect-square overflow-hidden">
                  <AppImage
                    src={product.image}
                    alt={product.alt || product.name}
                    className="h-full w-full object-cover transition-transform duration-250 group-hover:scale-105"
                  />
                </div>

                <div className="p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Icon name="TagIcon" size={14} className="text-text-secondary" />
                    <span className="font-caption text-xs text-text-secondary">
                      {categoryLabel}
                    </span>
                  </div>

                  <h3 className="mb-4 line-clamp-2 min-h-[3rem] font-heading text-base font-medium text-text-primary">
                    {product.name}
                  </h3>

                  {(pack || box) && (
                    <div className="space-y-2 border-t border-border pt-3">
                      {pack && (
                        <div className="flex items-center justify-between">
                          <span className="font-caption text-xs text-text-secondary">
                            {pack.label} ({pack.unitsPerOption} шт)
                          </span>
                          <span className="font-heading text-sm font-semibold text-primary">
                            {formatPrice(pack.basePrice)}
                          </span>
                        </div>
                      )}
                      {box && (
                        <div className="flex items-center justify-between">
                          <span className="font-caption text-xs text-text-secondary">
                            {box.label} ({box.unitsPerOption} шт)
                          </span>
                          <span className="font-heading text-sm font-semibold text-accent">
                            {formatPrice(box.basePrice)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 font-heading text-sm font-medium text-primary-foreground transition-colors duration-250 group-hover:bg-primary/90">
                    <Icon name="PlusIcon" size={16} />
                    <span>В корзину</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProducts;
